import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMParser as ProseMirrorDOMParser, Slice } from '@tiptap/pm/model';
import { cn } from '@/utils/cn';
import { createExtensions } from './extensions';
import { editorStyles, proseMirrorStyles } from './styles';
import { RichTextToolbar } from './RichTextToolbar';
import { transformPastedHTML, transformPastedText } from './pasteTransform';

export interface RichTextEditorProps {
  // Content
  value?: string;
  defaultValue?: string;
  onChange?: (html: string) => void;

  // Display
  label?: string;
  placeholder?: string;
  helpText?: string;
  error?: string;
  required?: boolean;

  // Layout
  minHeight?: number;
  maxHeight?: number;

  // Behavior
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;

  // Customization
  className?: string;
  toolbarClassName?: string;
  contentClassName?: string;
  showToolbar?: boolean;

  // React Hook Form support
  name?: string;
  onBlur?: () => void;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  defaultValue = '',
  onChange,
  label,
  placeholder = 'Start typing...',
  helpText,
  error,
  required = false,
  minHeight = 150,
  maxHeight,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  className,
  toolbarClassName,
  contentClassName,
  showToolbar = true,
  name,
  onBlur,
}) => {
  const extensions = createExtensions({ placeholder });

  const editor = useEditor({
    extensions,
    content: value ?? defaultValue,
    editable: !disabled && !readOnly,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange?.(html);
    },
    onBlur: () => {
      onBlur?.();
    },
    editorProps: {
      attributes: {
        class: cn(editorStyles.prose, 'focus:outline-none'),
        ...(name && { 'data-name': name }),
      },
      transformPastedHTML,
      // For plain-text pastes (e.g. PDFs that only put text/plain on the
      // clipboard) reconstruct paragraphs and lists by running the text
      // through transformPastedText to get HTML, then parsing that HTML
      // through the editor's schema. transformPastedText alone won't work
      // here — ProseMirror's transformPastedText hook treats its return
      // value as plain text and would escape the HTML.
      clipboardTextParser: (text, $context, _plain, view) => {
        const html = transformPastedText(text);
        if (!html) {return Slice.empty;}
        const container = document.implementation
          .createHTMLDocument('')
          .createElement('div');
        container.innerHTML = html;
        return ProseMirrorDOMParser.fromSchema(view.state.schema).parseSlice(
          container,
          { context: $context }
        );
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent = editor.getHTML();
      // Only update if content is actually different (avoid cursor jump)
      if (value !== currentContent) {
        editor.commands.setContent(value, { emitUpdate: false });
      }
    }
  }, [editor, value]);

  // Update editable state when disabled/readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [editor, disabled, readOnly]);

  const hasError = !!error;

  const contentStyle: React.CSSProperties = {
    minHeight: `${minHeight}px`,
    ...(maxHeight && { maxHeight: `${maxHeight}px` }),
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Inject ProseMirror styles */}
      <style>{proseMirrorStyles}</style>

      {label && (
        <label className={editorStyles.label}>
          {label}
          {required && <span className={editorStyles.required}>*</span>}
        </label>
      )}

      <div
        className={cn(
          editorStyles.wrapper,
          hasError && editorStyles.wrapperError,
          disabled && editorStyles.wrapperDisabled
        )}
      >
        {showToolbar && !readOnly && (
          <RichTextToolbar
            editor={editor}
            disabled={disabled}
            className={toolbarClassName}
          />
        )}

        <div
          className={cn(editorStyles.content, contentClassName)}
          style={contentStyle}
        >
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(error || helpText) && (
        <div>
          {error && <p className={editorStyles.errorText}>{error}</p>}
          {!error && helpText && <p className={editorStyles.helpText}>{helpText}</p>}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
